<script setup lang="ts">
import VCurrentSubscription from "@/scripts/components/app/billing/VCurrentSubscription.vue";
import VChangePlans from "@/scripts/components/app/billing/VChangePlans.vue";
import {computed, onMounted, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {useAccount} from "@/scripts/store/account";
import Loader from "@/scripts/components/app/common/Loader.vue";
import {CheckCircleIcon, ExclamationTriangleIcon} from '@heroicons/vue/20/solid'
import {PlanType} from "@/scripts/types/account";

export interface Props {
    preSelectedPlan: PlanType | null
}

const props = withDefaults(defineProps<Props>(), {
    preSelectedPlan: null,
});

let changePlanMode = ref(props.preSelectedPlan !== "null")

watch(() => props.preSelectedPlan, async () => {
    if (props.preSelectedPlan) {
        changePlanMode.value = true
    }
})

const accountStore = useAccount();
const {account} = storeToRefs(accountStore);

const loaded = ref(false)

const changePlanHandle = () => {
    changePlanMode.value = true
}

onMounted(async () => {
    await accountStore.getAccount()
    loaded.value = true
});

const callToActionTitle = computed(() => {
    return account.value.currentPlan.label === 'business'
        ? 'Продлить'
        : 'Продлить или сменить'
})

const hasBillingResult = ref(false)
const billingResult = ref(false)
let urlParams = new URLSearchParams(window.location.search);
if (urlParams.has('result')) {
    hasBillingResult.value = true
    billingResult.value = urlParams.get('result') === 'success'
}

</script>

<template>
    <div v-if="hasBillingResult">
        <div class="mb-8">
            <div class="rounded-md bg-yellow-50 p-4" v-if="!billingResult">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <ExclamationTriangleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true"/>
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-yellow-800">Оплата не прошла</h3>
                        <div class="mt-2 text-sm text-yellow-700">
                            <p>
                                Оплата не прошла. Попробуйте еще раз. Если проблема сохранится - пишите на
                                support@smartdots.ru, мы поможем.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-md bg-green-50 p-4" v-if="billingResult">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <CheckCircleIcon class="h-5 w-5 text-green-400" aria-hidden="true"/>
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-green-800">Оплата успешно прошла</h3>
                        <div class="mt-2 text-sm text-green-700">
                            <p>
                                Тарифный план успешно оплачен.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-if="loaded">
        <v-current-subscription :account="account"></v-current-subscription>
        <div class="mt-6" v-if="!changePlanMode">
            <button type="button"
                    @click="changePlanHandle"
                    class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500">
                {{ callToActionTitle }}
            </button>
        </div>
        <v-change-plans v-if="changePlanMode" :current-plan="account.currentPlan" :pre-selected-plan-id="preSelectedPlan"></v-change-plans>
    </div>
    <div v-else>
        <loader></loader>
    </div>
</template>